import type { Page } from '../../app/feature';
import { delegate, esc, q, qa } from '../../shared/dom';
import { getLang, t } from '../../shared/i18n';
import { dateTime, fmt } from '../../shared/format';
import { ApiError } from '../../shared/api/http';
import { hydrateFromApi } from '../../shared/store/sync';
import { toast } from '../../shared/ui/toast';
import { confirmDialog, promptDialog } from '../../shared/ui/dialog';
import { href } from '../../app/router';
import { ticketsApi, type Ticket, type TicketStatus } from './tickets.api';

let status: TicketStatus = 'PENDING';
let list: Ticket[] = [];
let counts: Record<TicketStatus, number> = { PENDING: 0, APPROVED: 0, REJECTED: 0 };
let loaded = false;
const imageUrls = new Map<string, string>();

/**
 * Validações: os prints que os jogadores enviam para as missões manuais (ex.: entrou na
 * comunidade, ativou as notificações). O suporte confere a imagem e aprova — o número nasce na
 * hora — ou rejeita com um motivo que o jogador vê no site.
 */
export const tickets: Page = {
  id: 'validaciones',
  pattern: '/validaciones',
  roles: ['ADMIN', 'SUPPORT'],
  nav: { key: 'nav.tickets', icon: '✅', group: 'ops' },

  template: () => `
<div class="view-head">
  <div>
    <h2 data-i18n="tk.title">Validaciones</h2>
    <p data-i18n="tk.sub2">Comprobantes que los jugadores envían desde el sitio para las misiones manuales. Mira la captura, aprueba (el número se emite al momento) o rechaza con un motivo que el jugador ve en su página.</p>
  </div>
</div>

<div class="cols" style="margin-bottom:18px">
  <div class="kpi span-4" style="--accent:var(--orange)"><div class="k-label" data-i18n="tk.kPending">Pendientes</div><div class="k-val tnum" id="tk-k-pending">0</div><div class="k-sub" data-i18n="tk.kPendingSub">esperando revisión</div></div>
  <div class="kpi span-4" style="--accent:var(--green)"><div class="k-label" data-i18n="tk.kApproved">Aprobados</div><div class="k-val tnum" id="tk-k-approved">0</div><div class="k-sub" data-i18n="tk.kApprovedSub">números liberados</div></div>
  <div class="kpi span-4" style="--accent:var(--line-2)"><div class="k-label" data-i18n="tk.kRejected">Rechazados</div><div class="k-val tnum" id="tk-k-rejected">0</div><div class="k-sub" data-i18n="tk.kRejectedSub">con motivo informado</div></div>
</div>

<div class="panel-head">
  <nav class="subnav" id="tk-tabs" style="margin:0">
    <a href="#" data-status="PENDING" class="on" data-i18n="tk.tabPending">Pendientes</a>
    <a href="#" data-status="APPROVED" data-i18n="tk.tabApproved">Aprobados</a>
    <a href="#" data-status="REJECTED" data-i18n="tk.tabRejected">Rechazados</a>
  </nav>
  <div class="spacer"></div>
  <span class="hint" data-i18n="tk.hint">Los comprobantes rechazados pueden reenviarse desde el sitio.</span>
</div>

<div class="tk-list" id="tk-list"></div>`,

  mount() {
    q('#tk-tabs').addEventListener('click', e => {
      const a = (e.target as Element).closest<HTMLAnchorElement>('a[data-status]');
      if (!a) return;
      e.preventDefault();
      status = a.dataset.status as TicketStatus;
      qa('#tk-tabs a').forEach(x => x.classList.toggle('on', x === a));
      void load();
    });

    const listEl = q('#tk-list');
    delegate(listEl, 'click', '[data-approve]', async el => {
      const tk = list.find(x => x.id === el.dataset.approve); if (!tk) return;
      const ok = await confirmDialog({
        title: t('tk.dlgApproveTitle'), icon: '✓', tone: 'go', confirmLabel: t('tk.dlgApproveBtn'),
        body: summary(tk) + `<p class="dlg-callout dlg-callout-go">🎟 ${esc(t('tk.dlgApproveIssue').replace('{qty}', String(tk.mission.qty)))}</p>
          <p class="dlg-hint">${esc(t('tk.dlgApproveHint'))}</p>`,
      });
      if (!ok) return;
      await run(() => ticketsApi.approve(tk.id), t('tk.approved'));
    });
    delegate(listEl, 'click', '[data-reject]', async el => {
      const tk = list.find(x => x.id === el.dataset.reject); if (!tk) return;
      const reason = await promptDialog({
        title: t('tk.dlgRejectTitle'), icon: '✕', tone: 'danger', confirmLabel: t('tk.dlgRejectBtn'),
        body: summary(tk), label: t('tk.dlgRejectLabel'), placeholder: t('tk.dlgRejectPh'), minLength: 3,
        suggestions: [t('tk.rs1'), t('tk.rs2'), t('tk.rs3')],
      });
      if (reason === null) return;
      await run(() => ticketsApi.reject(tk.id, reason), t('tk.rejected'));
    });
    delegate(listEl, 'click', '[data-del]', async el => {
      const tk = list.find(x => x.id === el.dataset.del); if (!tk) return;
      const ok = await confirmDialog({
        title: t('tk.dlgDeleteTitle'), icon: '🗑', tone: 'danger', confirmLabel: t('common.del'),
        body: summary(tk) + `<p class="dlg-hint">${esc(t('tk.confirmDelete'))}</p>`,
      });
      if (!ok) return;
      await run(() => ticketsApi.remove(tk.id), t('msg.saved'));
    });
    delegate(listEl, 'click', '[data-zoom]', el => {
      const url = imageUrls.get(el.dataset.zoom as string);
      if (url) window.open(url, '_blank', 'noopener');
    });
  },

  render() {
    paint();
    void load();
  }
};

/** Resumo do comprovante no topo do diálogo: a captura, quem enviou e para qual missão. */
function summary(tk: Ticket): string {
  const lang = getLang();
  const img = imageUrls.get(tk.id);
  return `<div class="dlg-ticket">
    <span class="dlg-shot">${img ? `<img src="${img}" alt="">` : '🖼'}</span>
    <span class="dlg-ticket-body">
      <b>${esc(tk.player.name)}</b>
      <small>ID ${esc(tk.player.externalId)} · ${esc(t('tk.sentAt'))} ${dateTime(Date.parse(tk.createdAt))}</small>
      <span class="chip chip-dim">${esc(lang === 'pt' ? tk.mission.labelPt : tk.mission.labelEs)} · ${tk.mission.qty} ${esc(t('tk.numbersShort'))}</span>
      ${tk.note ? `<em>“${esc(tk.note)}”</em>` : ''}
    </span>
  </div>`;
}

async function run(action: () => Promise<unknown>, msg: string): Promise<void> {
  try { await action(); toast(msg); await load(); void hydrateFromApi(); }
  catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
}

async function load(): Promise<void> {
  try {
    [list, counts] = await Promise.all([ticketsApi.list(status), ticketsApi.counts()]);
    loaded = true;
    paint();
    // imagens depois, sem travar a lista
    for (const tk of list) {
      if (imageUrls.has(tk.id)) continue;
      ticketsApi.imageUrl(tk.id).then(url => {
        imageUrls.set(tk.id, url);
        const img = document.querySelector<HTMLImageElement>(`img[data-ticket="${tk.id}"]`);
        if (img) img.src = url;
      }).catch(() => undefined);
    }
  } catch (err) {
    toast(err instanceof ApiError ? err.message : t('auth.errGeneric'));
  }
}

function paint(): void {
  q('#tk-k-pending').textContent = fmt(counts.PENDING);
  q('#tk-k-approved').textContent = fmt(counts.APPROVED);
  q('#tk-k-rejected').textContent = fmt(counts.REJECTED);
  const lang = getLang();

  q('#tk-list').innerHTML = list.map(tk => `
    <article class="tk-card ${tk.status.toLowerCase()}">
      <button type="button" class="tk-shot" data-zoom="${tk.id}" title="${esc(t('tk.zoom'))}">
        <img data-ticket="${tk.id}" alt="" ${imageUrls.has(tk.id) ? `src="${imageUrls.get(tk.id)}"` : ''}>
      </button>
      <div class="tk-body">
        <div class="inline" style="gap:8px; margin-bottom:6px">
          <span class="chip ${tk.status === 'APPROVED' ? 'chip-green' : tk.status === 'REJECTED' ? 'chip-dim' : 'chip-orange'}">${esc(t('tk.st.' + tk.status))}</span>
          <span class="chip chip-dim">${esc(lang === 'pt' ? tk.mission.labelPt : tk.mission.labelEs)} · ${tk.mission.qty} ${esc(t('tk.numbersShort'))}</span>
        </div>
        <div class="tk-who"><a class="auth-link" href="${href('/jugadores/' + tk.player.id)}"><b>${esc(tk.player.name)}</b></a> <span class="muted">· ID ${esc(tk.player.externalId)}</span></div>
        <div class="muted" style="font-size:12.5px">${esc(t('tk.sentAt'))} ${dateTime(Date.parse(tk.createdAt))}${tk.reviewedAt ? ` · ${esc(t('tk.reviewedAt'))} ${dateTime(Date.parse(tk.reviewedAt))}${tk.reviewedBy ? ` (${esc(tk.reviewedBy.name)})` : ''}` : ''}</div>
        ${tk.note ? `<p class="tk-note">“${esc(tk.note)}”</p>` : ''}
        ${tk.status === 'REJECTED' && tk.reason ? `<p class="tk-reason">✕ ${esc(tk.reason)}</p>` : ''}
        ${tk.status === 'APPROVED' ? `<p class="tk-ok">✓ ${tk.issuedCount} ${esc(t('tk.numbersShort'))}${tk.numbers.length ? ` · <span class="t-num">${tk.numbers.join(' ')}</span>` : ''}</p>` : ''}
      </div>
      <div class="tk-actions">
        ${tk.status === 'PENDING' ? `
          <button class="btn btn-primary" data-approve="${tk.id}">✓ ${esc(t('tk.approve'))}</button>
          <button class="btn btn-ghost danger" data-reject="${tk.id}">✕ ${esc(t('tk.reject'))}</button>` : ''}
        ${tk.status === 'REJECTED' ? `<button class="btn btn-sm btn-ghost" data-del="${tk.id}" title="${esc(t('common.del'))}">🗑</button>` : ''}
      </div>
    </article>`).join('') || `<div class="panel"><div class="empty">${esc(loaded ? t('tk.none.' + status) : '…')}</div></div>`;
}
